/**
 * One-click fixes offered next to a classified connection error.
 *
 * DESIGN DECISION (do not change): the app never silently retries with
 * weakened TLS settings. Loosening certificate validation (or toggling
 * encryption) is always a visible user action — the chip states exactly what
 * it changes; clicking it flips the setting and retries, and the setting is
 * kept only when the retry succeeds.
 *
 * Three chip shapes exist:
 * - APPLY fixes carry the field flips up front — clicking retries immediately.
 * - ACTION fixes open a small interactive Oracle flow inside the notice
 *   (set a new expired password / browse the listener's services); the flow
 *   ends by handing the caller a `ConnectionErrorFixRetry` with the fields to
 *   retry with, so both shapes converge on the same retry + persist-on-success
 *   machinery.
 * - NAVIGATE fixes route the user somewhere instead of retrying — the
 *   credentials and connection fixes open the connection editor, where saving
 *   already has its own connect/persist path. No retry, so no
 *   `connection_fix_result` event.
 */
import type { AnalyticsErrorCategory } from '../../../core/analytics/analyticsTypes';
import type { Connection } from '../../../types/electron';

export type ConnectionErrorApplyFixKind =
  | 'trust_server_certificate'
  | 'enable_encryption'
  | 'enable_ssl'
  | 'disable_ssl';

export type ConnectionErrorActionFixKind =
  | 'change_expired_password'
  | 'browse_listener_services';

export type ConnectionErrorNavigateFixKind = 'edit_credentials' | 'edit_connection';

/**
 * Kinds that end in a retry (navigate fixes never retry, so they stay out).
 * The analytics `fix` property carries the SUPERSET — type it as
 * `ConnectionErrorFix['kind']`, not this union.
 */
export type ConnectionErrorFixKind = ConnectionErrorApplyFixKind | ConnectionErrorActionFixKind;

/** Connection/form fields a fix retry may set (field names match both layers). */
export type ConnectionErrorFixApply = Partial<
  Pick<
    Connection,
    'encrypt' | 'trustServerCertificate' | 'ssl' | 'oracleServiceName' | 'oracleUseSID' | 'password'
  >
>;

/** Chip that flips settings and retries in one click. */
export type ConnectionErrorApplyFix = {
  kind: ConnectionErrorApplyFixKind;
  /** Chip label — states the retry AND the trade-off, so the action is transparent. */
  label: string;
  apply: ConnectionErrorFixApply;
};

/** Chip that opens an interactive Oracle flow inside the notice. */
export type ConnectionErrorActionFix = {
  kind: ConnectionErrorActionFixKind;
  label: string;
};

/** Chip that routes to another surface (the connection editor). */
export type ConnectionErrorNavigateFix = {
  kind: ConnectionErrorNavigateFixKind;
  label: string;
};

export type ConnectionErrorFix =
  | ConnectionErrorApplyFix
  | ConnectionErrorActionFix
  | ConnectionErrorNavigateFix;

/**
 * What the notice hands back to `onApplyFix` — the fields to retry with.
 * For apply fixes this is the chip's own `apply`; for action fixes it is the
 * outcome of the flow (the new password / the picked service name).
 */
export type ConnectionErrorFixRetry = {
  kind: ConnectionErrorFixKind;
  apply: ConnectionErrorFixApply;
  /**
   * Keep the applied fields even when the retry fails. Only the expired-
   * password fix sets this: by retry time the server-side password has
   * already changed, so reverting the field would leave a value that can
   * never connect again.
   */
  keepOnFailure?: boolean;
};

export function isApplyConnectionErrorFix(fix: ConnectionErrorFix): fix is ConnectionErrorApplyFix {
  return 'apply' in fix;
}

export function isNavigateConnectionErrorFix(
  fix: ConnectionErrorFix
): fix is ConnectionErrorNavigateFix {
  return fix.kind === 'edit_credentials' || fix.kind === 'edit_connection';
}

/**
 * ORA codes where Oracle's password-change-at-connect is the actual fix.
 * ORA-28002 (grace period) is defensive: the thin driver usually surfaces it
 * as `connection.warning` on a SUCCESSFUL connect, so in practice only
 * ORA-28001 reaches this failure-path notice — don't expect 28002 clicks in
 * telemetry.
 */
const ORACLE_EXPIRED_PASSWORD_CODES = new Set(['ORA-28001', 'ORA-28002']);
/**
 * Codes meaning the listener answered but knows no such service/SID. The thin
 * driver usually reports this as its own NJS-518 (measured against a real
 * listener) rather than the server's ORA-12514/ORA-12505 — all three occur.
 */
const ORACLE_UNKNOWN_SERVICE_CODES = new Set(['ORA-12514', 'ORA-12505', 'NJS-518']);

/**
 * Map a classified connection failure to the one-click fix that most
 * likely resolves it, honouring which TLS fields each engine's form exposes
 * (mssql: encrypt + trustServerCertificate; mysql: ssl + trustServerCertificate;
 * pgsql: ssl only — the PG driver already accepts self-signed certificates).
 *
 * `context.trustServerCertificate` is the CURRENT value on the failing
 * connection: enabling MySQL SSL while trust is already on means the retry
 * will not validate the certificate, and the label must say so.
 *
 * `context.code` is the sanitized driver code from `classifyDbError` — the
 * Oracle fixes key on the exact ORA code because one category (auth_failed,
 * instance_not_found) covers conditions with different remedies.
 */
export function getConnectionErrorFix(
  category: AnalyticsErrorCategory,
  engine: string | undefined,
  context?: {
    trustServerCertificate?: boolean;
    /** Sanitized driver error code from the classification (e.g. ORA-28001). */
    code?: string;
    /** Gates the listener-services fix to EZConnect host:port connections. */
    oracleConnectionStringType?: Connection['oracleConnectionStringType'];
    /**
     * Withholds the listener-services fix for SSH-tunneled connections: the
     * discovery probe dials `server:port` directly from this machine, which a
     * tunneled target by definition doesn't answer.
     */
    sshTunnelEnabled?: boolean;
    /**
     * Gates the editor-route fixes (credentials / connection) to surfaces
     * that can open the connection editor (the connections list). The
     * connection dialog never sets this — the user is already looking at the
     * editable fields there.
     */
    canEditConnection?: boolean;
    /**
     * The failing connection's authentication mode. Modes without
     * user/password form fields (Windows auth, Entra, "No password") must not
     * be sent to an editor that has neither field to update.
     */
    authentication?: Connection['authentication'];
  }
): ConnectionErrorFix | undefined {
  if (category === 'tls_cert_untrusted' && (engine === 'mssql' || engine === 'mysql')) {
    return {
      kind: 'trust_server_certificate',
      label: 'Retry trusting the server certificate (less secure)',
      apply: { trustServerCertificate: true },
    };
  }

  if (category === 'tls_required') {
    if (engine === 'mssql') {
      return {
        kind: 'enable_encryption',
        label: 'Retry with encryption enabled',
        apply: { encrypt: true },
      };
    }
    if (engine === 'pgsql' || engine === 'mysql') {
      const trustAlreadyOn = engine === 'mysql' && context?.trustServerCertificate === true;
      return {
        kind: 'enable_ssl',
        label: trustAlreadyOn
          ? 'Retry with SSL enabled, trusting the server certificate (less secure)'
          : 'Retry with SSL enabled',
        apply: { ssl: true },
      };
    }
  }

  if (category === 'tls_unsupported' && (engine === 'pgsql' || engine === 'mysql')) {
    return {
      kind: 'disable_ssl',
      label: 'Retry without SSL (server does not support it)',
      apply: { ssl: false },
    };
  }

  if (engine === 'oracle' && context?.code) {
    // Expired (or expiring, in the grace period) password — Oracle lets the
    // user set a new one during connect, so offer that instead of a dead end.
    if (category === 'auth_failed' && ORACLE_EXPIRED_PASSWORD_CODES.has(context.code)) {
      return { kind: 'change_expired_password', label: 'Set a new password…' };
    }

    // The listener answered but knows no such service/SID — the existing
    // service discovery can list what IS registered there. Only meaningful
    // for EZConnect host:port connections (TNS descriptor/alias addresses
    // don't expose a probe-able host/port pair in the form fields).
    if (
      category === 'instance_not_found' &&
      ORACLE_UNKNOWN_SERVICE_CODES.has(context.code) &&
      (context.oracleConnectionStringType ?? 'ezconnect') === 'ezconnect' &&
      context.sshTunnelEnabled !== true
    ) {
      return { kind: 'browse_listener_services', label: 'Browse services on this listener…' };
    }
  }

  // Wrong user or password (2026-08 telemetry: the top no-fix category on
  // connection error notices). Placed AFTER the Oracle block so an expired
  // password (ORA-28001, also auth_failed) keeps its in-place fix. Only for
  // engines AND auth modes whose form actually has user/password fields —
  // a Windows-auth/Entra/no-password failure must not route to an editor
  // where neither field exists.
  if (
    category === 'auth_failed' &&
    context?.canEditConnection &&
    NETWORK_ENGINES.has(engine) &&
    !(context.authentication && NON_CREDENTIAL_AUTH_MODES.has(context.authentication))
  ) {
    return { kind: 'edit_credentials', label: 'Update user & password…' };
  }

  // The server never answered, or answered "no such database" (2026-08
  // telemetry: after auth_failed, these four categories are the remaining
  // no-fix bulk on connection notices). There is no field to flip and retry —
  // the remedy is correcting the server/port/database (or noticing the VPN is
  // off), so route to the editor. Every auth mode has those fields, so unlike
  // the credentials fix this one is not auth-mode gated.
  if (
    REACHABILITY_CATEGORIES.has(category) &&
    context?.canEditConnection &&
    NETWORK_ENGINES.has(engine)
  ) {
    return { kind: 'edit_connection', label: 'Edit connection…' };
  }

  return undefined;
}

/**
 * Engines whose form has network target fields (server/port). SQLite opens a
 * file — the reachability categories don't occur for it, and the credentials
 * fix has no user/password row to send anyone to.
 */
const NETWORK_ENGINES = new Set<string | undefined>(['mssql', 'pgsql', 'mysql', 'oracle']);

/** Categories meaning the configured target could not be reached or is absent. */
const REACHABILITY_CATEGORIES = new Set<AnalyticsErrorCategory>([
  'timeout',
  'conn_refused',
  'dns_not_found',
  'db_not_found',
]);

/**
 * Authentication modes whose connection form hides the login/password row
 * (mirrors `requiresCredentials` in ConnectionFormFields.tsx, inverted so an
 * absent value on an older saved record keeps the fix available — every
 * legacy record predating auth-mode variety used user/password).
 */
const NON_CREDENTIAL_AUTH_MODES = new Set<Connection['authentication']>([
  'Windows authentication',
  'Microsoft Entra ID (Device Code)',
  'Microsoft Entra ID (Interactive Browser)',
  'No password',
  'Azure CLI',
]);
